import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import StatusBadge from "../components/StatusBadge";
import { toast } from "sonner";
import { Plus } from "@phosphor-icons/react";

function fmt(s) {
  return s ? new Date(s).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—";
}

export default function History() {
  const { user } = useAuth();
  const [records, setRecords] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    reason: "forgot_clock_in",
    requested_clock_in: "",
    requested_clock_out: "",
    note: "",
  });

  const load = async () => {
    try {
      const { data } = await api.get("/attendance", { params: { user_id: user.id, limit: 200 } });
      setRecords(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/corrections", form);
      toast.success("Correction request submitted");
      setShowForm(false);
      setForm({ ...form, note: "", requested_clock_in: "", requested_clock_out: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="px-4 md:px-10 py-8 max-w-6xl mx-auto" data-testid="history-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="label-eyebrow">My attendance</div>
          <h1 className="font-heading font-black text-4xl tracking-tighter text-zinc-950 mt-1">History</h1>
          <p className="text-zinc-600 mt-1 text-sm">Your last {records.length} attendance records.</p>
        </div>
        <button
          data-testid="open-correction-form-btn"
          onClick={() => setShowForm((v) => !v)}
          className="flex items-center gap-2 px-4 py-2.5 bg-zinc-950 hover:bg-zinc-800 text-white text-sm font-semibold rounded-sm"
        >
          <Plus size={14} weight="bold" />
          Request Correction
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="bg-white border border-zinc-200 rounded-sm p-5 mb-6 grid sm:grid-cols-2 gap-4" data-testid="correction-form">
          <div className="sm:col-span-2 label-eyebrow">New correction request</div>
          <Field label="Date">
            <input
              type="date"
              required
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              className="lom-input"
              data-testid="correction-date-input"
            />
          </Field>
          <Field label="Reason">
            <select
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              className="lom-input"
              data-testid="correction-reason-select"
            >
              <option value="forgot_clock_in">Forgot to clock in</option>
              <option value="forgot_clock_out">Forgot to clock out</option>
              <option value="gps_issue">GPS issue</option>
              <option value="other">Other</option>
            </select>
          </Field>
          <Field label="Requested clock-in (HH:MM)">
            <input
              type="time"
              value={form.requested_clock_in}
              onChange={(e) => setForm({ ...form, requested_clock_in: e.target.value })}
              className="lom-input"
              data-testid="correction-clockin-input"
            />
          </Field>
          <Field label="Requested clock-out (HH:MM)">
            <input
              type="time"
              value={form.requested_clock_out}
              onChange={(e) => setForm({ ...form, requested_clock_out: e.target.value })}
              className="lom-input"
              data-testid="correction-clockout-input"
            />
          </Field>
          <Field label="Note (required)" full>
            <textarea
              required
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              rows={3}
              className="lom-input resize-none"
              placeholder="Briefly explain what happened"
              data-testid="correction-note-input"
            />
          </Field>
          <div className="sm:col-span-2 flex gap-2 justify-end">
            <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 border border-zinc-300 rounded-sm text-sm" data-testid="correction-cancel-btn">Cancel</button>
            <button type="submit" className="px-4 py-2 bg-zinc-950 hover:bg-zinc-800 text-white rounded-sm text-sm" data-testid="correction-submit-btn">Submit Request</button>
          </div>
        </form>
      )}

      <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr>
              <Th>Date</Th>
              <Th>Status</Th>
              <Th className="text-right">Clock In</Th>
              <Th className="text-right">Clock Out</Th>
              <Th className="text-right">Worked</Th>
              <Th className="text-right">Late</Th>
              <Th>Store</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {records.length === 0 && (
              <tr><td colSpan={7} className="text-center text-zinc-500 py-8">No records yet</td></tr>
            )}
            {records.map((r, i) => (
              <tr key={r.id} className={i % 2 === 0 ? "bg-white" : "bg-zinc-50/40"} data-testid={`history-row-${i}`}>
                <Td className="font-mono">{r.date}</Td>
                <Td><StatusBadge status={r.status} /></Td>
                <Td className="text-right font-mono tabular-nums">{fmt(r.clock_in_at)}</Td>
                <Td className="text-right font-mono tabular-nums">{fmt(r.clock_out_at)}</Td>
                <Td className="text-right font-mono tabular-nums">
                  {r.worked_minutes ? `${Math.floor(r.worked_minutes/60)}h ${r.worked_minutes%60}m` : "—"}
                </Td>
                <Td className="text-right font-mono tabular-nums">{r.late_minutes ? `${r.late_minutes}m` : "—"}</Td>
                <Td className="text-zinc-700">{r.store_name || "—"}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const Th = ({ children, className = "" }) => (
  <th className={`px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-zinc-500 ${className}`}>{children}</th>
);
const Td = ({ children, className = "" }) => (
  <td className={`px-4 py-3 text-zinc-900 ${className}`}>{children}</td>
);
const Field = ({ label, children, full }) => (
  <div className={full ? "sm:col-span-2" : ""}>
    <label className="label-eyebrow block mb-1.5">{label}</label>
    {children}
  </div>
);
